import { paymentStatusLabel } from '@klear/payment/presentation'
import type { Payable } from '@klear/payable/payable'
import { PaymentNetworkOptions } from '@klear/payment/constants'
import { Badge } from '@klear/ui/components/badge'
import { DataTable } from '@klear/ui/components/data-table'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { isEventLine } from '@klear/payment/line-item'
import { trpc } from '@klear/ui/lib/trpc'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, Paperclip } from 'lucide-react'
import { PayableActions } from '@/app/payables/payable-actions'
import { usePaymentReceipts, type PaymentReceipt } from '@/app/payable/use-payment-receipts'
import { useOperationalPayments } from '@klear/ui/hooks/use-payables'

function PayableReceiptBadge({ receipt }: { receipt: PaymentReceipt }) {
  const { startDownload, isDownloading } = useFileDownload()

  return (
    <button
      type="button"
      className="inline-flex"
      disabled={isDownloading}
      onClick={() => startDownload(receipt.url, receipt.name)}
    >
      <Badge variant="secondary" className="gap-1 cursor-pointer">
        {receipt.name}
        <Paperclip className="h-3 w-3" />
      </Badge>
    </button>
  )
}

export function OperationalPayments({
  operationalEventId,
  traderId
}: {
  operationalEventId: number
  traderId: number
  orderId: number
}) {
  const utils = trpc.useUtils()
  const { payments } = useOperationalPayments(operationalEventId, traderId)
  const receiptsFor = usePaymentReceipts(
    payments.map((payment) => payment.paymentId ?? payment.id),
    traderId
  )

  const handleActionSuccess = () => {
    void utils.payable.paymentsForOperationalEvent.invalidate()
    void utils.payable.list.invalidate()
  }

  if (payments.length === 0) {
    return <p className="text-sm text-muted-foreground">No payments yet</p>
  }

  const columns: ColumnDef<Payable>[] = [
    {
      accessorKey: 'idDisplay',
      header: 'ID',
      cell: ({ row }) => <Badge variant="outline">{row.original.idDisplay}</Badge>
    },
    {
      accessorKey: 'effectiveDate',
      header: 'Payment Date',
      cell: ({ row }) => row.original.effectiveDate.toString()
    },
    { accessorKey: 'number', header: 'Number', cell: ({ row }) => row.original.number ?? '—' },
    {
      accessorKey: 'paymentNetwork',
      header: 'Network',
      cell: ({ row }) => PaymentNetworkOptions.find((network) => network.value === row.original.paymentNetwork)?.label
    },
    {
      accessorKey: 'sourceAccount',
      header: 'Source Account',
      cell: ({ row }) => row.original.sourceAccountOrganizationName
    },
    {
      accessorKey: 'destinationAccount',
      header: 'Destination Account',
      cell: ({ row }) => {
        const name = row.original.destinationAccountOrganizationName
        return row.original.last4 ? `${name} - ${row.original.last4}` : name
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.paymentStatus ?? row.original.status
        return <Badge variant="secondary">{paymentStatusLabel(status)}</Badge>
      }
    },
    {
      accessorKey: 'amount',
      header: 'Payment Amount',
      cell: ({ row }) => {
        const coveredCount = row.original.lineItems.filter(isEventLine).length
        return (
          <span className="whitespace-nowrap">
            {row.original.amount.toString()}
            {coveredCount > 1 ? (
              <span className="ml-1 text-xs text-muted-foreground">· total of {coveredCount} events</span>
            ) : null}
          </span>
        )
      }
    },
    {
      accessorKey: 'receiptUrl',
      header: 'Receipt',
      cell: ({ row }) =>
        row.original.receiptUrl ? (
          <a
            href={row.original.receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            View Receipt
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : null
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) =>
        row.original.notes ? (
          <span className="whitespace-pre-wrap text-sm text-muted-foreground">{row.original.notes}</span>
        ) : (
          '—'
        )
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const receipts = receiptsFor(row.original.paymentId ?? row.original.id)
        return (
          <div className="flex items-center justify-end gap-2">
            {receipts.map((receipt) => (
              <PayableReceiptBadge key={receipt.id} receipt={receipt} />
            ))}
            {!row.original.isSent && (
              <PayableActions payable={row.original} traderId={traderId} onActionSuccess={handleActionSuccess} />
            )}
          </div>
        )
      }
    }
  ]

  return <DataTable showFilters={false} showPagination={false} columns={columns} data={payments} />
}
